/** E2E object encryption. See docs/e2e.md; transport integration is a separate rollout gate. */
import { copy, fromBase64Url, randomBytes, toBase64Url, utf8, } from './bytes.js';
import { domainSeparated } from './hash.js';
import { canonicalize } from './jcs.js';
export const MAX_CONTENT_BYTES = 16 * 1024 * 1024;
const BUCKET = 256;
const subtle = () => globalThis.crypto.subtle;
export function exactKeys(value, keys) {
    if (Object.keys(value).length !== keys.length ||
        Object.keys(value).some((k) => !keys.includes(k))) {
        throw new Error('unexpected encryption fields');
    }
}
export function validId(value) {
    if (typeof value !== 'string' || !value.length || value.length > 256)
        throw new Error('invalid encryption id');
}
export function positiveInteger(value) {
    if (!Number.isSafeInteger(value) || value < 1)
        throw new Error('invalid encryption counter');
}
export function validateContentContext(c) {
    exactKeys(c, ['version', 'accountId', 'epoch', 'objectId', 'runId', 'seq', 'kind']);
    if (c.version !== 1)
        throw new Error('unsupported content version');
    for (const s of [c.accountId, c.objectId, c.runId, c.kind])
        validId(s);
    positiveInteger(c.epoch);
    positiveInteger(c.seq);
}
export function createAccountDataKey() {
    return randomBytes(32);
}
export async function importAccountDataKey(raw) {
    if (raw.length !== 32)
        throw new Error('ADK must be 256 bits');
    return subtle().importKey('raw', copy(raw), 'HKDF', false, ['deriveKey', 'deriveBits']);
}
/** RFC 5869 SHA-256, 32-byte output. Intended for high entropy input, not passwords. */
export async function hkdf256(ikm, salt, info) {
    const key = await subtle().importKey('raw', copy(ikm), 'HKDF', false, ['deriveBits']);
    return new Uint8Array(await subtle().deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: copy(salt), info: copy(info) }, key, 256));
}
export async function importAesKey(raw) {
    if (raw.length !== 32)
        throw new Error('AES key must be 256 bits');
    return subtle().importKey('raw', copy(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}
async function objectKey(adk, context) {
    validateContentContext(context);
    return subtle().deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt: utf8('viko/content/hkdf/v1'),
        info: utf8(canonicalize(context)),
    }, adk, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
/** Low-level authenticated box, also used for ADK wrapping with distinct AAD domains. */
export async function sealBox(key, aad, plaintext) {
    if (plaintext.length > MAX_CONTENT_BYTES)
        throw new Error('content too large');
    const iv = randomBytes(12);
    const ciphertext = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 }, key, copy(plaintext));
    return {
        version: 1,
        algorithm: 'A256GCM',
        iv: toBase64Url(iv),
        ciphertext: toBase64Url(new Uint8Array(ciphertext)),
    };
}
export async function openBox(key, aad, box) {
    exactKeys(box, ['version', 'algorithm', 'iv', 'ciphertext']);
    if (box.version !== 1 ||
        box.algorithm !== 'A256GCM' ||
        typeof box.iv !== 'string' ||
        typeof box.ciphertext !== 'string')
        throw new Error('invalid content box');
    if (box.iv.length !== 16 || box.ciphertext.length > Math.ceil(((MAX_CONTENT_BYTES + 16) * 4) / 3))
        throw new Error('invalid content length');
    const iv = fromBase64Url(box.iv);
    const ciphertext = fromBase64Url(box.ciphertext);
    if (iv.length !== 12 || ciphertext.length < 16)
        throw new Error('invalid content length');
    return new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 }, key, ciphertext));
}
export async function sealContent(adk, context, plaintext) {
    const key = await objectKey(adk, context);
    if (plaintext.length > MAX_CONTENT_BYTES - BUCKET)
        throw new Error('content too large');
    const padded = new Uint8Array(Math.ceil((plaintext.length + 4) / BUCKET) * BUCKET);
    new DataView(padded.buffer).setUint32(0, plaintext.length);
    padded.set(plaintext, 4);
    return sealBox(key, domainSeparated('viko/content/aad/v1', context), padded);
}
export async function openContent(adk, context, box) {
    const key = await objectKey(adk, context);
    const padded = await openBox(key, domainSeparated('viko/content/aad/v1', context), box);
    if (padded.length < BUCKET || padded.length % BUCKET !== 0)
        throw new Error('invalid content padding');
    const n = new DataView(padded.buffer).getUint32(0);
    if (n > padded.length - 4 ||
        padded.length - 4 - n >= BUCKET ||
        padded.subarray(n + 4).some((b) => b !== 0))
        throw new Error('invalid content padding');
    return padded.slice(4, n + 4);
}
/** Strict ordered action reader. Paging uses openContent; callers persist checkpoints locally. */
export class ContentReader {
    #checkpoint;
    #busy = false;
    constructor(accountId, runId, checkpoint) {
        validId(accountId);
        validId(runId);
        if (checkpoint &&
            (checkpoint.accountId !== accountId ||
                checkpoint.runId !== runId ||
                !Number.isSafeInteger(checkpoint.seq) ||
                checkpoint.seq < 0))
            throw new Error('invalid checkpoint');
        this.#checkpoint = checkpoint ? { ...checkpoint } : { accountId, runId, seq: 0 };
    }
    get checkpoint() {
        return { ...this.#checkpoint };
    }
    async accept(key, context, box) {
        if (this.#busy)
            throw new Error('serialize content reader calls');
        this.#busy = true;
        try {
            const c = this.#checkpoint;
            if (context.accountId !== c.accountId || context.runId !== c.runId)
                throw new Error('wrong content stream');
            if (context.seq <= c.seq)
                throw new Error('content replay');
            if (context.seq !== c.seq + 1)
                throw new Error('content gap');
            const bytes = await openContent(key, context, box);
            this.#checkpoint = { ...c, seq: context.seq };
            return bytes;
        }
        finally {
            this.#busy = false;
        }
    }
}
//# sourceMappingURL=content.js.map